import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useRef, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { MessageCircle, Book } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface HelpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HelpModal({ open, onOpenChange }: HelpModalProps) {
  useLanguage();
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);

  const handleChatClick = () => {
    // Abre o chat do Crisp
    if (window.$crisp) {
      window.$crisp.push(["do", "chat:open"]);
    }
    onOpenChange(false);
  };

  const handleHelpCenterClick = () => {
    navigate('/help');
    onOpenChange(false);
  };

  // Click outside to close modal
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node) && open) {
        onOpenChange(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onOpenChange, open]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent 
        ref={modalRef}
        className="sm:max-w-md"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center text-xl">
            {translateInterfaceText("Como podemos te ajudar?")}</AlertDialogTitle>
          <p className="text-center text-muted-foreground text-sm mb-2">
            {translateInterfaceText("Escolha uma das opções abaixo para receber suporte.")}</p>
        </AlertDialogHeader>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
          <Button
            variant="outline"
            className="flex flex-col items-center justify-center h-auto py-5 px-4 gap-3 hover:bg-[#F4F9FF] hover:border-[#007BFF] data-[state=open]:bg-[#007BFF] data-[state=open]:text-white transition-all"
            onClick={handleChatClick}
            aria-label={translateInterfaceText("Chat com suporte")}
          >
            <MessageCircle className="h-8 w-8 text-primary" />
            <span className="font-medium">{translateInterfaceText("Chat com Suporte")}</span>
          </Button>
          
          <Button
            variant="outline"
            className="flex flex-col items-center justify-center h-auto py-5 px-4 gap-3 hover:bg-[#F4F9FF] hover:border-[#007BFF] data-[state=open]:bg-[#007BFF] data-[state=open]:text-white transition-all"
            onClick={handleHelpCenterClick}
            aria-label={translateInterfaceText("Acessar central de ajuda")}
          >
            <Book className="h-8 w-8 text-primary" />
            <span className="font-medium">{translateInterfaceText("Central de Ajuda")}</span>
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
